/*
 *  *
 *  *  * Copyright (c) 2023 Hg Digital, Setera
 *  *  *************************************************************
 *  *  *Projeto  : setera-web
 *  *  *Autor    : diegobrock
 *  *  *Data     : 04/07/2023 16:58
 *  *  *Empresa  : HG Digital/Setera
 *  *  *************************************************************
 *  *
 */

import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { HttpMethod } from "../enums/http-method";
import { HttpUtil } from "../utils/http-util";
import { switchMap } from "rxjs/operators";
import {LinkModel} from '../models/hateoas/link-model';

@Injectable({
  providedIn: "root",
})
export abstract class HateoasBaseService {
  headers = new HttpHeaders();
  private apiIndex: any = null;

  protected constructor(private http: HttpClient, private httpUtil: HttpUtil) {}

  private loadApiIndex(parentResource: any): Observable<any> {
    return new Observable<any>((observer) => {
      if (this.apiIndex === null && parentResource === null) {
        this.getApiIndex().subscribe((apiIndex) => {
          this.apiIndex = apiIndex;
          observer.next(apiIndex);
        });
      }
      if (parentResource != null) {
        observer.next(parentResource);
      }
      if (this.apiIndex != null && parentResource === null) {
        observer.next(this.apiIndex);
      }
    });
  }

  executeActionOnResource(
    rel: string,
    name: string,
    parentResource: any,
    request: any,
    parameters: Map<string, string> | null
  ): Observable<any> {
    return this.loadApiIndex(parentResource).pipe(
      switchMap((apiIndex) => {
        let links: Array<any> = [];

        if(apiIndex._links !== undefined && apiIndex._links[rel] instanceof Array) {
          links = apiIndex._links[rel];
        }
        if(apiIndex._links !== undefined && !(apiIndex._links[rel] instanceof Array)) {
          const entry = apiIndex._links[rel];
          // Anota rel/name quando o mapa Spring só traz { href } (ex.: _links.next).
          links = entry
            ? Array.of({ ...entry, rel: entry.rel || rel, name: entry.name || name })
            : [];
        }

        // Índice flat (`links[]`): exige name+rel, exceto paginação HAL (name === rel).
        if(apiIndex._links === undefined && apiIndex.links !== undefined){
          links = Array.of(apiIndex.links.find((link: LinkModel) => {
            return this.matchesHateoasLink(link, rel, name);
          }));
        }

        const link = links.find((entry) => {
          return this.matchesHateoasLink(entry, rel, name);
        });
        if (!link || !link.href) {
          return throwError(new Error('Link HATEOAS não encontrado: ' + name));
        }
        return this.dispatchHateoasLink(link, request, parameters);
      })
    );
  }

  /**
   * Segue uma ação nomeada embutida no recurso (ex.: resend/cancel no item).
   * Paridade com setera-http `followAction` e setera-admin-web: usa o href/type
   * do link encontrado, sem reconsultar o índice da API.
   *
   * @param resource item HAL com `links` ou `_links`
   * @param actionName nome da ação (`resend`, `cancel`, etc.)
   * @param request corpo opcional (PATCH/POST/PUT)
   */
  followNamedAction(
    resource: any,
    actionName: string,
    request: any = null
  ): Observable<any> {
    const link = this.findNamedLink(resource, actionName);
    if (!link || !link.href) {
      return throwError(new Error('Ação não disponível para este comando: ' + actionName));
    }
    return this.dispatchHateoasLink(link, request, null);
  }

  /**
   * Localiza link por `name` (fallback: `rel` igual ao nome da ação).
   *
   * @param resource recurso com links HATEOAS
   * @param actionName nome da ação
   */
  private findNamedLink(resource: any, actionName: string): LinkModel | null {
    const links = this.normalizeResourceLinks(resource);
    return (
      links.find(
        (entry) => entry && (entry.name === actionName || entry.rel === actionName)
      ) || null
    );
  }

  /**
   * Normaliza `links[]` (Setera) e `_links` (HAL mapa ou array) em lista plana.
   *
   * @param resource recurso HATEOAS
   */
  private normalizeResourceLinks(resource: any): LinkModel[] {
    if (!resource) {
      return [];
    }
    if (Array.isArray(resource.links)) {
      return resource.links;
    }
    if (Array.isArray(resource._links)) {
      return resource._links;
    }
    if (resource._links && typeof resource._links === 'object') {
      const result: LinkModel[] = [];
      Object.keys(resource._links).forEach((key) => {
        const value = (resource._links as Record<string, LinkModel | LinkModel[]>)[key];
        if (Array.isArray(value)) {
          value.forEach((entry) => {
            if (entry) {
              result.push({
                ...entry,
                rel: entry.rel || key,
                name: entry.name || key,
              });
            }
          });
          return;
        }
        if (value) {
          result.push({
            ...value,
            rel: value.rel || key,
            name: value.name || key,
          });
        }
      });
      return result;
    }
    return [];
  }

  /**
   * Expande template (se houver), reescreve o href do gateway e dispara o HTTP.
   *
   * @param link link HATEOAS com href e type
   * @param request corpo da requisição
   * @param parameters query/path params para URI template
   */
  private dispatchHateoasLink(
    link: LinkModel | any,
    request: any,
    parameters: Map<string, string> | null
  ): Observable<any> {
    let linkString = link.href;

    if ((link.templated || link.href.includes('{')) && parameters !== null) {
      const supportedParameters = linkString.substring(linkString.indexOf('{?') + 2, linkString.lastIndexOf('}')).split(',');
      linkString = linkString.replaceAll(',', '&');
      for (const p of parameters.entries()) {
        const paramName = p[0];
        const paramValue = p[1];
        // Match do nome do parâmetro como token (evita colidir "page" dentro de outros trechos).
        const token = new RegExp('([?&{])' + paramName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?=&|}|$)');
        const locationOfParameter = linkString.lastIndexOf(paramName);
        if (linkString[locationOfParameter - 1] == '{'
            && linkString[locationOfParameter + paramName.length] == '}') {
          linkString = linkString.replace('{' + paramName + '}', paramValue);
          continue;
        }
        linkString = linkString.replace(token, '$1' + paramName + '=' + paramValue);
      }
      linkString = linkString.replace('{', '');
      linkString = linkString.replace('}', '');
      // Se algum parametro supportado não foi preenchido, remove da string
      for (const p of supportedParameters) {
        if (!parameters.has(p)) {
          linkString = linkString.replace('&' + p, '');
        }
      }
    }

    if (link.templated && parameters === null) {
      linkString = linkString.substring(0, linkString.indexOf('{'));
    }

    linkString = this.transformLink(linkString);
    const method = link.type || HttpMethod[HttpMethod.GET];

    switch (method) {
      case HttpMethod[HttpMethod.GET]:
        return this.getResource(linkString);
      case HttpMethod[HttpMethod.POST]:
        return this.postResource(linkString, request);
      case HttpMethod[HttpMethod.PUT]:
        return this.putResource(linkString, request);
      case HttpMethod[HttpMethod.DELETE]:
        return this.deleteResource(linkString);
      case HttpMethod[HttpMethod.PATCH]:
        return this.patchResource(linkString, request);
      default:
        return this.getResource(linkString);
    }
  }

  /**
   * Indica se o link HAL corresponde à ação pedida.
   * - Ações nomeadas: `name` obrigatório; `rel` só restringe quando o link o informa.
   * - Paginação (first/next/prev/last): `name === rel`, aceita link só com `href`
   *   obtido via `_links[rel]`.
   *
   * @param link entrada HAL
   * @param rel relação do recurso/ação
   * @param name nome da ação
   */
  private matchesHateoasLink(link: LinkModel | any, rel: string, name: string): boolean {
    if (!link) {
      return false;
    }
    if (link.name === name) {
      return link.rel == null || link.rel === '' || link.rel === rel;
    }
    // Paginação Spring HAL: exige rel explícito (evita confundir `self` com `next`).
    if (name === rel && (link.rel === rel || link.rel === name)) {
      return true;
    }
    // Link anotado a partir de `_links[rel]` (name preenchido no lookup).
    if (name === rel && link.name === name) {
      return true;
    }
    return false;
  }

  private getApiIndex(): Observable<any> {
    return this.http.get<any>(this.getApiBaseUrl().concat("links"), {
      headers: this.buildHeaders(),
    });
  }

  private getResource(link: string): Observable<any> {
    return this.http.get<any>(link, { headers: this.buildHeaders() });
  }

  private postResource(link: string, request: any): Observable<any> {
    return this.http.post<any>(link, request, { headers: this.buildHeaders() });
  }

  private putResource(link: string, request: any): Observable<any> {
    return this.http.put<any>(link, request, { headers: this.buildHeaders() });
  }

  private patchResource(link: string, request: any): Observable<any> {
    return this.http.patch<any>(link, request, {
      headers: this.buildHeaders(),
    });
  }

  private deleteResource(link: string): Observable<any> {
    return this.http.delete<any>(link, { headers: this.buildHeaders() });
  }

  // TODO - Identificar o motivo dos header X-forwarded não serem interpretados corretamente quando o microserviço é publicado no openshift
  /**
   * Reescreve o host/path do microserviço para a URL do gateway (`apiBaseUrl`).
   * Não altera hrefs que já começam com `apiBaseUrl` (evita prefixo duplicado
   * em links de item gerados com forward-headers).
   *
   * @param link href original do HATEOAS
   */
  private transformLink(link: string): string {
    const apiBase = this.getApiBaseUrl();
    const microBase = this.getMicroServiceBaseUrl();
    if (!link || link.indexOf(apiBase) === 0) {
      return link;
    }
    const newLink = link.replace(microBase, apiBase);
    console.log(newLink);
    return newLink;
  }

  /**
   * Método responsavel pelo processamento das query parameters da URL
   * @param queryParams
   * @param parameters
   * @private
   */
  private processQueryParams(
    queryParams: string,
    parameters: Map<string, string>
  ): string {
    let queryParamsProcessed = "?";
    for (const p of parameters.entries()) {
      // se o parametro está na lista de query parameters
      if (queryParams.includes(p[0])) {
        // Monta o trecho da url correspondente
        let qp = p[0] + "=" + p[1];
        if (queryParamsProcessed !== "?") {
          qp = "&" + qp;
        }
        queryParamsProcessed = queryParamsProcessed.concat(qp);
      }
    }

    return queryParamsProcessed;
  }

  protected abstract getApiBaseUrl(): string;

  protected abstract getMicroServiceBaseUrl(): string;

  protected buildHeaders(): HttpHeaders {
    return this.httpUtil.buildHeadersGateway();
  }
}
